import { useState, useEffect } from "react";
import { API, useAuth, authFetch } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Users, Disc3, Star, LayoutList, Heart, 
  TrendingUp, Search, Loader2, Shield, Calendar, Activity,
  UserPlus, BarChart3, Ban, Trash2, UserX, CheckCircle2
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [usersTotal, setUsersTotal] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, target: null });
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, usersRes, activityRes] = await Promise.all([
        authFetch(`${API}/admin/stats`),
        authFetch(`${API}/admin/users?limit=20`),
        authFetch(`${API}/admin/activity?limit=20`)
      ]);
      if (statsRes.status === 403) {
        setError("You don't have admin access. Contact the administrator to be added.");
        setLoading(false);
        return;
      }
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users);
        setUsersTotal(usersData.total);
      }
      if (activityRes.ok) setActivity(await activityRes.json());
    } catch {
      setError("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const res = await authFetch(`${API}/admin/users?search=${encodeURIComponent(searchQuery)}&limit=50`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setUsersTotal(data.total);
      }
    } catch {}
  };

  const handleToggleDisable = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await authFetch(`${API}/admin/users/${userId}/toggle-disable`, { method: "PUT" });
      if (res.ok) {
        const data = await res.json();
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, disabled: data.disabled } : u));
        toast.success(data.message);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update user");
      }
    } catch {
      toast.error("Failed to update user");
    } finally {
      setActionLoading(null);
      setConfirmDialog({ open: false, type: null, target: null });
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await authFetch(`${API}/admin/users/${userId}`, { method: "DELETE" });
      if (res.ok) {
        setUsers(prev => prev.filter(u => u.user_id !== userId));
        setUsersTotal(prev => prev - 1);
        toast.success("User deleted");
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete user");
      }
    } catch {
      toast.error("Failed to delete user");
    } finally {
      setActionLoading(null);
      setConfirmDialog({ open: false, type: null, target: null });
    }
  };

  const handleDeleteLog = async (logId) => {
    setActionLoading(logId);
    try {
      const res = await authFetch(`${API}/admin/logs/${logId}`, { method: "DELETE" });
      if (res.ok) {
        setActivity(prev => prev.filter(l => l.log_id !== logId));
        toast.success("Review removed");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to remove review");
      }
    } catch {
      toast.error("Failed to remove review");
    } finally {
      setActionLoading(null);
      setConfirmDialog({ open: false, type: null, target: null });
    }
  };

  const openConfirm = (type, target) => setConfirmDialog({ open: true, type, target });

  const executeConfirm = () => {
    const { type, target } = confirmDialog;
    if (type === "disable") handleToggleDisable(target.user_id);
    else if (type === "delete-user") handleDeleteUser(target.user_id);
    else if (type === "delete-log") handleDeleteLog(target.log_id);
  };

  const getConfirmText = () => {
    const { type, target } = confirmDialog;
    if (!target) return {};
    if (type === "disable") {
      const isDisabled = target.disabled;
      return {
        title: isDisabled ? "Enable User Account" : "Disable User Account",
        description: isDisabled
          ? `Re-enable ${target.name}'s account? They will be able to log in again.`
          : `Disable ${target.name}'s account? They will be logged out and unable to access Sonary.`,
        action: isDisabled ? "Enable Account" : "Disable Account",
        destructive: !isDisabled
      };
    }
    if (type === "delete-user") return {
      title: "Delete User Permanently",
      description: `Permanently delete ${target.name} and ALL their data (logs, reviews, lists, favorites)? This cannot be undone.`,
      action: "Delete User",
      destructive: true
    };
    if (type === "delete-log") return {
      title: "Remove Review",
      description: `Remove this log/review by ${target.user?.name || "user"}? This cannot be undone.`,
      action: "Remove",
      destructive: true
    };
    return {};
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0510] pt-36 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0510] pt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
            <p className="text-white/60">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const confirm = getConfirmText();

  return (
    <div className="min-h-screen bg-[#0A0510] pt-36 pb-12" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-purple-400" />
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-white/60">Platform statistics and moderation</p>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/20"><Users className="w-5 h-5 text-purple-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.overview.total_users}</p>
                    <p className="text-xs text-white/50">Total Users</p>
                  </div>
                </div>
                <p className="text-xs text-green-400 mt-2">+{stats.overview.new_users_week} this week</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20"><Disc3 className="w-5 h-5 text-blue-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.overview.total_logs}</p>
                    <p className="text-xs text-white/50">Albums Logged</p>
                  </div>
                </div>
                <p className="text-xs text-green-400 mt-2">+{stats.overview.logs_this_week} this week</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-yellow-500/20"><Star className="w-5 h-5 text-yellow-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.overview.total_reviews}</p>
                    <p className="text-xs text-white/50">Reviews</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20"><LayoutList className="w-5 h-5 text-green-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.overview.total_lists}</p>
                    <p className="text-xs text-white/50">Lists</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-pink-500/20"><Heart className="w-5 h-5 text-pink-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.overview.total_favorites}</p>
                    <p className="text-xs text-white/50">Favorites</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-purple-950/50 p-1 rounded-full mb-6">
            <TabsTrigger value="overview" className="rounded-full text-white/70 data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-full text-white/70 data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Users ({usersTotal})
            </TabsTrigger>
            <TabsTrigger value="activity" className="rounded-full text-white/70 data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <Activity className="w-4 h-4 mr-2" /> Activity
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-400" /> Top Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats?.top_users?.map((item, index) => (
                      <Link key={item.user.user_id} to={`/profile/${item.user.user_id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
                        <span className="text-white/40 w-6 text-center">{index + 1}</span>
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={item.user.picture} />
                          <AvatarFallback className="bg-purple-500/20 text-purple-400 text-sm">{item.user.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{item.user.name}</p>
                          <p className="text-xs text-white/50 truncate">{item.user.email}</p>
                        </div>
                        <Badge className="bg-purple-500/20 text-purple-300">{item.log_count} logs</Badge>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Disc3 className="w-5 h-5 text-blue-400" /> Most Logged Albums
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats?.top_albums?.map((item, index) => (
                      <div key={item.album?.album_id || index} className="flex items-center gap-3 p-2 rounded-lg">
                        <span className="text-white/40 w-6 text-center">{index + 1}</span>
                        <img src={item.album?.cover_medium || item.album?.cover_url || ""} alt={item.album?.title} className="w-10 h-10 rounded object-cover bg-white/5"
                          onError={(e) => { e.target.src = "https://placehold.co/40x40/1a1a2e/9333ea?text=?"; }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{item.album?.title || "Unknown"}</p>
                          <p className="text-xs text-white/50 truncate">{item.album?.artist || "Unknown"}</p>
                        </div>
                        <Badge className="bg-blue-500/20 text-blue-300">{item.log_count} logs</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10 md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-green-400" /> Recent Signups
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {stats?.recent_users?.map((u) => (
                      <Link key={u.user_id} to={`/profile/${u.user_id}`} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="bg-purple-500/20 text-purple-400">{u.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{u.name}</p>
                          <p className="text-xs text-white/50 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {u.created_at && formatDistanceToNow(new Date(u.created_at), { addSuffix: true })}
                          </p>
                        </div>
                        <Badge className={u.auth_type === "email" ? "bg-blue-500/20 text-blue-300" : "bg-green-500/20 text-green-300"}>
                          {u.auth_type === "email" ? "Email" : "Google"}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab with Moderation */}
          <TabsContent value="users">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <CardTitle className="text-white">All Users</CardTitle>
                  <div className="flex-1 flex gap-2">
                    <Input placeholder="Search by name or email..." value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      className="max-w-sm bg-white/5 border-white/10 text-white"
                      data-testid="admin-user-search" />
                    <Button onClick={handleSearch} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="admin-search-btn">
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {users.map((u) => (
                    <div key={u.user_id} className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${u.disabled ? "bg-red-500/5 border border-red-500/20" : "hover:bg-white/5"}`}
                      data-testid={`admin-user-${u.user_id}`}>
                      <Link to={`/profile/${u.user_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="bg-purple-500/20 text-purple-400">{u.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{u.name}</p>
                            {u.disabled && <Badge className="bg-red-500/20 text-red-300 text-[10px]">Disabled</Badge>}
                          </div>
                          <p className="text-xs text-white/50">{u.email}</p>
                        </div>
                      </Link>
                      <div className="flex items-center gap-4 text-sm text-white/60">
                        <span>{u.log_count} logs</span>
                        <span>{u.list_count} lists</span>
                        <span>{u.follower_count} followers</span>
                      </div>
                      <Badge className={u.auth_type === "email" ? "bg-blue-500/20 text-blue-300" : "bg-green-500/20 text-green-300"}>
                        {u.auth_type === "email" ? "Email" : "Google"}
                      </Badge>
                      {/* Moderation Actions */}
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openConfirm("disable", u)}
                          disabled={actionLoading === u.user_id}
                          className={u.disabled ? "text-green-400 hover:text-green-300 hover:bg-green-500/10" : "text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"}
                          title={u.disabled ? "Enable account" : "Disable account"}
                          data-testid={`admin-toggle-disable-${u.user_id}`}>
                          {u.disabled ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openConfirm("delete-user", u)}
                          disabled={actionLoading === u.user_id}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          title="Delete user permanently"
                          data-testid={`admin-delete-user-${u.user_id}`}>
                          <UserX className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab with Review Removal */}
          <TabsContent value="activity">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {activity.map((log) => (
                    <div key={log.log_id} className="flex items-center gap-4 p-3 rounded-lg bg-white/5"
                      data-testid={`admin-activity-${log.log_id}`}>
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={log.user?.picture} />
                        <AvatarFallback className="bg-purple-500/20 text-purple-400">{log.user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <img src={log.album?.cover_medium || log.album?.cover_url || ""} alt={log.album?.title}
                        className="w-12 h-12 rounded object-cover bg-white/5"
                        onError={(e) => { e.target.src = "https://placehold.co/48x48/1a1a2e/9333ea?text=?"; }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white">
                          <span className="font-medium">{log.user?.name}</span>{" logged "}
                          <span className="font-medium">{log.album?.title}</span>
                        </p>
                        <p className="text-xs text-white/50">
                          {log.album?.artist} {log.listened_at && `\u2022 ${formatDistanceToNow(new Date(log.listened_at), { addSuffix: true })}`}
                        </p>
                        {log.review && <p className="text-xs text-white/40 mt-1 line-clamp-1">{log.review}</p>}
                      </div>
                      {log.rating && (
                        <Badge className="bg-yellow-500/20 text-yellow-300">
                          <Star className="w-3 h-3 mr-1 fill-current" /> {log.rating}
                        </Badge>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openConfirm("delete-log", log)}
                        disabled={actionLoading === log.log_id}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        title="Remove this log/review"
                        data-testid={`admin-delete-log-${log.log_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, target: null })}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{confirm.title}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-white/70 text-sm">{confirm.description}</DialogDescription>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, type: null, target: null })}
              className="border-white/20 text-white hover:bg-white/10" data-testid="admin-confirm-cancel">
              Cancel
            </Button>
            <Button onClick={executeConfirm} disabled={actionLoading}
              className={confirm.destructive ? "bg-red-600 hover:bg-red-700 text-white" : "bg-green-600 hover:bg-green-700 text-white"}
              data-testid="admin-confirm-action">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {confirm.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
